import { ApplicationInsights, type ICustomProperties, type ITelemetryItem } from "@microsoft/applicationinsights-web"
import { ReactPlugin } from "@microsoft/applicationinsights-react-js"
import { config } from "./utils/config"

type ExtendedCustomProperties = Partial<ICustomProperties> & {
  name: string,
  target: string
}

// Escape special regex characters in a string
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

let apiRegex: RegExp

function telemetryInitializer(envelope: ITelemetryItem): boolean | void {
  if (envelope.tags) {
    envelope.tags["ai.cloud.role"] =
      `${config.app}${!config.instrumentationEnvironment ? "" : `-${config.instrumentationEnvironment || "Local"}`}`
  }

  if (envelope.baseType !== "RemoteDependencyData") {
    return true
  }

  const telemetryItem = { ...envelope.baseData, ...envelope.data } as ExtendedCustomProperties

  // Don't log options
  if (telemetryItem.name.toUpperCase().startsWith("OPTIONS ")) {
    return false
  }

  // Track unity review api 404s and prevent the logging of the 404, any real 404s are
  // logged in the axios worker
  if (telemetryItem.responseCode === 404 && apiRegex!.test(telemetryItem.target)) {
    return false
  }

  return true
}

export function createInsightsSettings(): { reactPlugin: ReactPlugin; isTelemetryEnabled: boolean } {
  const isTelemetryEnabled = !!config.instrumentationKey
  console.info(`Telemetry enabled: ${isTelemetryEnabled}`)
  let reactPlugin: ReactPlugin
  let appInsights

  if (isTelemetryEnabled) {
    apiRegex = new RegExp(escapeRegExp(config.apiUrl))

    reactPlugin = new ReactPlugin()
    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: config.instrumentationKey,
        extensions: [reactPlugin],
        enableAutoRouteTracking: true, // Optionally track route changes
        maxBatchInterval: 0, // Send telemetry immediately
        disableFetchTracking: false, // Enable fetch tracking
        enableCorsCorrelation: true, // Enable CORS correlation
        enableRequestHeaderTracking: true, // Enable request header tracking
        enableResponseHeaderTracking: true, // Enable response header tracking
        disableAjaxTracking: false, // Disable AJAX tracking
        // DO NOT DISABLE the ajax tracking, we ARE tracking manually
        // BUT without the automatic tracking, there are NO correlation headers added
        isCookieUseDisabled: false, // Enable cookie usage,
        // Disable the deprecated 'unload' event to avoid jQuery 3.7.1+ deprecation warnings
        // This also prevents Chrome's warnings about the unload event
        disablePageUnloadEvents: ["unload"],
      },
    })

    // Add the telemetry initializer to set the cloud role name
    appInsights.addTelemetryInitializer(telemetryInitializer)

    appInsights.loadAppInsights()
  } else {
    // If disabled, create mock objects with empty methods
    reactPlugin = {
      appInsights: {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        trackEvent: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        trackPageView: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        trackException: () => {},
      },
    } as any as ReactPlugin
  }

  return { reactPlugin, isTelemetryEnabled }
}
